<template>
  <div class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center" @click.self="$emit('close')">
    <div class="bg-white rounded-lg shadow-xl border border-gray-200 p-6 w-full max-w-md">
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-xl font-bold text-gray-800">Edit Player Status</h3>
        <button @click="$emit('close')" class="text-gray-400 hover:text-gray-700 text-lg transition-colors">
          <i class="fas fa-times"></i>
        </button>
      </div>
      
      <div class="mb-4">
        <span class="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-full px-3 py-1 font-semibold text-xs shadow-sm">
          {{ player?.player?.player_name || player?.player_name }}
        </span>
      </div>

      <div class="mb-4">
        <label class="block text-sm font-medium text-gray-700 mb-2">
          Status
        </label>

        <PlayerStatusDropdown
          v-if="!isLoading"
          :player-status="currentStatusId"
          :value="currentStatusId"
          :player-id="player?.player?.id"
          @status-change="handleStatusChange"
        />
        <div v-else class="px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 flex items-center">
          <i class="fas fa-spinner fa-spin mr-2"></i>
          <span class="text-sm text-gray-500">Loading status options...</span>
        </div>
        <p class="text-xs text-gray-500 mt-1">Current: {{ currentStatusLabel || 'Not set' }}</p>
      </div>

      <div class="flex gap-3 justify-end">
        <button
          @click="$emit('close')"
          class="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-semibold text-sm transition-colors"
        >
          Cancel
        </button>
        <button
          @click="saveStatus"
          :disabled="saving || selectedStatusId === currentStatusId"
          class="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-lg font-semibold text-sm shadow-sm transition-all hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span v-if="saving">Saving...</span>
          <span v-else>Save Status</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import axios from 'axios'
import PlayerStatusDropdown from './PlayerStatusDropdown.vue'

const props = defineProps({
  player: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['close', 'update:player'])

const currentStatusId = ref(null)
const selectedStatusId = ref(null)
const currentStatusLabel = ref('')
const saving = ref(false)
const isLoading = ref(true)
const statusOptions = ref([])

// Get current status ID - prioritize new player_team_status.player_status_id, fallback to old player_status.id
function getPlayerStatusId() {
  if (!props.player?.player) return null
  
  // Prioritize the new player_team_status.player_status_id
  if (props.player.player.player_team_status?.player_status_id !== undefined && props.player.player.player_team_status?.player_status_id !== null) {
    return props.player.player.player_team_status.player_status_id
  }
  // Fallback to old version only when new version is missing
  if (props.player.player.player_status?.id !== undefined && props.player.player.player_status?.id !== null) {
    return props.player.player.player_status.id
  }
  return null
}

// Get current status label
function getPlayerStatusLabel() {
  if (!props.player?.player) return ''
  
  // Try to get from player_team_status.player_status.name
  if (props.player.player.player_team_status?.player_status?.name) {
    return props.player.player.player_team_status.player_status.name
  }
  // Try to get from player_status.name
  if (props.player.player.player_status?.name) {
    return props.player.player.player_status.name
  }
  return ''
}

async function fetchStatusOptions() {
  try {
    isLoading.value = true
    const response = await axios.get('/api/v2/projections-players/player-statuses')
    
    if (response.data && Array.isArray(response.data)) {
      statusOptions.value = response.data.map(item => ({
        value: item.id || item.value,
        label: item.name || item.label
      }))
      
      // Set current status label
      const statusId = getPlayerStatusId()
      const statusOption = statusOptions.value.find(opt => opt.value === statusId)
      if (statusOption) {
        currentStatusLabel.value = statusOption.label
      } else {
        currentStatusLabel.value = getPlayerStatusLabel() || 'Not set'
      }
    }
  } catch (error) {
    console.error('Error fetching status options:', error)
  } finally {
    isLoading.value = false
  }
}

function handleStatusChange(data) {
  selectedStatusId.value = data.status
}

onMounted(() => {
  currentStatusId.value = getPlayerStatusId()
  selectedStatusId.value = currentStatusId.value
  fetchStatusOptions()
})

async function saveStatus() {
  if (saving.value || selectedStatusId.value === null || selectedStatusId.value === currentStatusId.value) return
  
  saving.value = true
  
  try {
    const playerId = props.player.player.id
    
    // Use the same API endpoint as PlayerStatusDropdown
    const response = await axios.put(`/api/v2/projections-players/teams/player/${playerId}/status`, {
      player_status: selectedStatusId.value,
      player_id: playerId
    })

    if (response.data.success || response.data) {
      // Update the player object
      const updatedPlayer = { ...props.player }
      if (updatedPlayer.player) {
        // Update player_team_status if it exists
        if (!updatedPlayer.player.player_team_status) {
          updatedPlayer.player.player_team_status = {}
        }
        
        // Update the player_status_id
        updatedPlayer.player.player_team_status.player_status_id = selectedStatusId.value
        
        // Update the nested player_status object
        if (!updatedPlayer.player.player_team_status.player_status) {
          updatedPlayer.player.player_team_status.player_status = {}
        }
        
        // Get status label from response or statusOptions
        const statusOption = statusOptions.value.find(opt => opt.value === selectedStatusId.value)
        if (statusOption) {
          updatedPlayer.player.player_team_status.player_status.id = selectedStatusId.value
          updatedPlayer.player.player_team_status.player_status.name = statusOption.label
        }
        
        // Also update old player_status for backward compatibility
        if (!updatedPlayer.player.player_status) {
          updatedPlayer.player.player_status = {}
        }
        updatedPlayer.player.player_status.id = selectedStatusId.value
        if (statusOption) {
          updatedPlayer.player.player_status.name = statusOption.label
        }
      }
      
      emit('update:player', updatedPlayer)
      emit('close')
    }
  } catch (error) {
    console.error('Error updating player status:', error)
    alert('Failed to update player status. Please try again.')
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
</style>
